"use client";

/**
 * Terminal and transient model-generation status bubbles shown between the
 * message history and the next interactive prompt.
 *  - busy + !isDone        → thinkingText (e.g. "Thinking…")
 *  - isDone + pending      → "Generating model…"
 *  - isDone + failed       → "Model generation failed."
 *  - otherwise             → null
 */

import { AiBubble } from "./AiBubble";

export type ModelSnapshotStatus = "pending" | "done" | "failed";

export interface IntakeModelStatusProps {
  /** Whether the graph is actively processing a turn. */
  busy: boolean;
  /** Whether the graph has reached its terminal phase (done). */
  isDone: boolean;
  modelSnapshot: ModelSnapshotStatus;
  thinkingText?: string;
}

export function IntakeModelStatus({
  busy,
  isDone,
  modelSnapshot,
  thinkingText = "Thinking…",
}: IntakeModelStatusProps) {
  if (isDone && modelSnapshot === "pending") {
    return (
      <AiBubble>
        <span className="text-(--fg-muted)">Generating model…</span>
      </AiBubble>
    );
  }

  if (isDone && modelSnapshot === "failed") {
    return <AiBubble>Model generation failed.</AiBubble>;
  }

  if (busy && !isDone) {
    return (
      <AiBubble>
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-[14px] w-[14px] shrink-0 animate-spin rounded-full border-2 border-(--border-default) border-t-(--fg-brand)"
          />
          <span className="text-(--fg-muted)">{thinkingText}</span>
        </span>
      </AiBubble>
    );
  }

  return null;
}
